// components/RealTimeSearch.tsx

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { dataService } from "@/services/dataService";
import type { Course, CourseApiResponse } from "@/types/api";
import {
  ArrowRight,
  BookOpen,
  Search,
  TrendingUp,
  X
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface SearchResult {
  id: string;
  title: string;
  type: "course";
  description: string;
  featured?: boolean;
}

interface RealTimeSearchProps {
  isMobile?: boolean;
  onClose?: () => void;
}

export default function RealTimeSearch({ isMobile = false, onClose }: RealTimeSearchProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  const debounce = (fn: Function, delay: number) => {
    let timer: ReturnType<typeof setTimeout>;
    return (...args: unknown[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  };

  const fetchResults = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      setIsOpen(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const keywords = term.trim().split(/\s+/).join(",");
      const response: CourseApiResponse = await dataService.searchCourseKeywords(
        keywords,
        1,
        6
      );
      const courses: SearchResult[] = response.data.data.map((course: Course) => ({
        id: course.id.toString(),
        title: course.title,
        type: "course",
        description: course.description,
        featured: course.rating > 4.5,
      }));
      setResults(courses);
      setIsOpen(courses.length > 0);
      setSelectedIndex(-1);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(debounce(fetchResults, 300), [fetchResults]);

  useEffect(() => {
    debouncedFetch(searchTerm);
  }, [searchTerm, debouncedFetch]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || !results.length) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(i => (i < results.length - 1 ? i + 1 : i));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(i => (i > 0 ? i - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) onSelect(results[selectedIndex]);
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const onSelect = (item: SearchResult) => {
    navigate(`/search?q=${encodeURIComponent(item.title)}`);
    reset();
  };

  const handleClear = () => {
    reset();
    inputRef.current?.focus();
  };

  const reset = () => {
    setSearchTerm("");
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    onClose?.();
  };

  const getTypeConfig = (type: string) =>
    type === "course"
      ? { color: "bg-blue-100 text-blue-800 border-blue-200", icon: BookOpen, label: "Course" }
      : { color: "bg-gray-100 text-gray-800 border-gray-200", icon: Search, label: "Result" };

  return (
    <div ref={searchRef} className={`relative ${isMobile ? "w-full" : "w-64"}`}>
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
          <Search className="h-4 w-4 text-ecosafe-gray" />
          {!isMobile && (
            <div className="w-4 h-3 flex flex-col gap-0.5">
              <div className="h-0.5 bg-ecosafe-green/30 rounded" />
              <div className="h-0.5 bg-ecosafe-green/30 rounded" />
              <div className="h-0.5 bg-ecosafe-green/30 rounded" />
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search courses..."
          className={`${isMobile ? "pl-10 pr-10" : "pl-16 pr-10"} py-3 ${isMobile ? "w-full" : "w-64"} border-2 border-ecosafe-green/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-ecosafe-green/30 focus:border-ecosafe-green bg-white shadow-sm placeholder:text-ecosafe-gray/60 text-ecosafe-black font-medium transition-all duration-300 hover:border-ecosafe-green/40`}
        />
        {searchTerm && (
          <button onClick={handleClear} className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-ecosafe-green/5 text-ecosafe-gray hover:text-ecosafe-green-dark transition-colors duration-200">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {loading && isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 p-4 text-center text-ecosafe-gray text-sm">
          Loading…
        </div>
      )}

      {error && (
        <div className="absolute top-full left-0 right-0 mt-2 p-4 text-center text-red-600 text-sm">
          {error}
        </div>
      )}

      {isOpen && !loading && results.length > 0 && (
        <Card className="absolute top-full left-0 right-0 mt-3 z-50 shadow-2xl border-0 bg-white/95 backdrop-blur-xl overflow-hidden">
          <CardContent className="p-0">
            <div className="px-4 py-3 bg-gradient-to-r from-ecosafe-green/5 to-ecosafe-green/10 border-b border-ecosafe-green/10 flex items-center gap-2">
              <div className="w-4 h-3 flex flex-col gap-0.5">
                <div className="h-0.5 bg-ecosafe-green rounded" />
                <div className="h-0.5 bg-ecosafe-green rounded" />
                <div className="h-0.5 bg-ecosafe-green rounded" />
              </div>
              <span className="text-xs font-bold text-ecosafe-black">ECOSAFE SEARCH RESULTS</span>
              <Badge className="bg-ecosafe-green/10 text-ecosafe-green border-ecosafe-green/20 text-xs">{results.length} found</Badge>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {results.map((r, i) => {
                const { color, icon: Icon, label } = getTypeConfig(r.type);
                const selected = i === selectedIndex;
                return (
                  <div
                    key={r.id}
                    onClick={() => onSelect(r)}
                    className={`flex items-center justify-between p-4 cursor-pointer border-b border-ecosafe-green/5 last:border-b-0 transition-all duration-200 group ${selected ? "bg-ecosafe-green/10 border-l-4 border-l-ecosafe-green" : "hover:bg-ecosafe-green/5 hover:border-l-4 hover:border-l-ecosafe-green/30"}`}
                  >
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`${color} w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 group-hover:scale-110`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-ecosafe-black line-clamp-1 group-hover:text-ecosafe-green-dark transition-colors">
                            {r.title}
                          </h4>
                          <Badge className={`${color} border text-xs font-medium`}>{label}</Badge>
                          {r.featured && (
                            <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              Popular
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-ecosafe-gray leading-relaxed line-clamp-1">{r.description}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-ecosafe-green opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-200" />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-4 py-3 bg-ecosafe-green/5 border-t border-ecosafe-green/10">
              <div className="flex items-center justify-between text-xs">
                <span className="text-ecosafe-gray">Use ↑↓ to navigate, Enter to select, Esc to close</span>
                <span className="text-ecosafe-green font-medium">Train to Lead, Learn with ECOSAFE</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isOpen && !loading && !results.length && !error && (
        <Card className="absolute top-full left-0 right-0 mt-3 z-50 shadow-2xl border-0 bg-white/95 backdrop-blur-xl">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-ecosafe-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="h-6 w-6 text-ecosafe-green" />
            </div>
            <h4 className="font-semibold text-ecosafe-black mb-2">No results found</h4>
            <p className="text-sm text-ecosafe-gray mb-4">Try different keywords</p>
            <div className="flex items-center justify-center gap-2 text-xs text-ecosafe-green">
              <div className="w-3 h-2 flex flex-col gap-0.5">
                <div className="h-0.5 bg-ecosafe-green rounded" />
                <div className="h-0.5 bg-ecosafe-green rounded" />
                <div className="h-0.5 bg-ecosafe-green rounded" />
              </div>
              <span className="font-medium">ECOSAFE Training</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
